import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { Button, Field, Input, cx } from '../ui/kit'

/**
 * Ausstattungsmerkmale einer Einheit.
 *
 * Zwei Ebenen, weil es zwei Fragen sind: WELCHE Merkmale kennt dieser Betrieb
 * überhaupt (die Liste, einmal gepflegt), und welche davon hat DIESE Einheit (die
 * Häkchen, je Einheit). Ohne die erste Ebene entstehen sechs Schreibweisen von WLAN
 * und niemand kann danach filtern; ohne die zweite müsste jede Einheit ihre eigene
 * Liste tippen.
 *
 * Neue Merkmale entstehen genau hier, beim Ankreuzen — nicht in einer entfernten
 * Verwaltungsmaske. Wer beim Anlegen einer Wohnung merkt, dass „Sauna" fehlt, tippt
 * es ein, und ab dann kennt der Betrieb es. Das ist der Moment, in dem man es weiß.
 */
export default function AmenityPicker({
  value, onChange,
}: { value: string[]; onChange: (next: string[]) => void }) {
  const { lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const [adding, setAdding] = useState('')

  const catalogue = store.db.tenant.amenity_catalogue ?? []
  // Was an dieser Einheit hängt, aber (noch) nicht im Katalog steht — etwa nach einem
  // Import. Es soll sichtbar bleiben, statt beim nächsten Speichern zu verschwinden.
  const all = [...new Set([...catalogue, ...value])].sort((a, b) => a.localeCompare(b))

  const toggle = (name: string) => {
    onChange(value.includes(name)
      ? value.filter((v) => v !== name)
      : [...value, name])
  }

  const add = async () => {
    const name = adding.trim()
    if (!name) return
    setAdding('')
    if (!catalogue.some((c) => c.toLowerCase() === name.toLowerCase())) {
      await store.saveTenant({ amenity_catalogue: [...catalogue, name] })
    }
    if (!value.some((v) => v.toLowerCase() === name.toLowerCase())) {
      onChange([...value, name])
    }
  }

  return (
    <Field
      label={de ? 'Ausstattung' : 'Amenities'}
      hint={de
        ? 'Erscheint auf der Anfrageseite und im Angebot.'
        : 'Shown on the enquiry page and in quotes.'}
    >
      <div className="space-y-2">
        {all.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {all.map((name) => {
              const on = value.includes(name)
              return (
                <button
                  key={name}
                  onClick={() => toggle(name)}
                  className={cx(
                    'px-2.5 py-1.5 rounded-xl text-sm border',
                    on
                      ? 'border-accent bg-accent-soft text-accent font-medium'
                      : 'border-border bg-surface-2 text-muted',
                  )}
                >
                  {name}
                  {/* Aus dem Katalog nehmen, nicht nur abwählen — sonst wächst die
                      Liste mit jedem Tippfehler und wird nie wieder kürzer. */}
                  {!on && (
                    <span
                      role="button"
                      aria-label={de ? `„${name}" aus der Liste entfernen` : `Remove “${name}”`}
                      className="ml-1.5 opacity-50 hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!window.confirm(de
                          ? `„${name}" ganz aus der Liste des Betriebs entfernen?`
                          : `Remove “${name}” from the whole list?`)) return
                        void store.saveTenant({
                          amenity_catalogue: catalogue.filter((c) => c !== name),
                        })
                      }}
                    >
                      <X size={12} className="inline -mt-0.5" />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        <div className="flex gap-2">
          <Input
            value={adding}
            placeholder={de ? 'WLAN, Balkon, Sauna …' : 'Wifi, balcony, sauna …'}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add() } }}
          />
          <Button variant="secondary" size="md" disabled={!adding.trim()}
            onClick={() => void add()}>
            <Plus size={16} />
          </Button>
        </div>
      </div>
    </Field>
  )
}
